import { useMemo, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { calculateAllSegmentWTPCeilings } from '../../lib/calculations';
import { formatPrice, formatPercent } from '../../lib/formatters';
import { GRID_PROPS, X_AXIS_PROPS, Y_AXIS_PROPS, TOOLTIP_STYLE } from '../../styles/chart-theme';
import { InfoButton } from '../common/Tooltip';
import { useIsMobile } from '../../hooks/useIsMobile';

function BarLabel(props: { x?: number; y?: number; width?: number; value?: number }) {
  const { x = 0, y = 0, width = 0, value } = props;
  const cx = x + width / 2;
  if (value === 0) {
    return (
      <text x={cx} y={y - 8} textAnchor="middle" fill="var(--color-text-muted)" fontSize={11} fontStyle="italic">
        N/A
      </text>
    );
  }
  return (
    <text x={cx} y={y - 8} textAnchor="middle" fill="var(--color-text-primary)" fontSize={12} fontWeight={700}>
      {formatPrice(value!)}
    </text>
  );
}

export function WTPBySegment() {
  const isMobile = useIsMobile();
  const [threshold, setThreshold] = useState(0.10);
  const data = useMemo(() => calculateAllSegmentWTPCeilings(threshold), [threshold]);

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <h3 className="text-heading font-semibold text-secondary uppercase tracking-wider">Maximum Price by Segment</h3>
          <InfoButton helpKey="wtpBySegment" size={13} />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-micro text-muted">Demand threshold:</span>
          <select
            value={threshold}
            onChange={e => setThreshold(parseFloat(e.target.value))}
            className="text-xs px-2 py-1 rounded border border-border bg-transparent text-primary font-mono"
          >
            <option value={0.05}>5%</option>
            <option value={0.10}>10%</option>
            <option value={0.15}>15%</option>
            <option value={0.20}>20%</option>
            <option value={0.25}>25%</option>
          </select>
        </div>
      </div>
      <p className="text-sm text-muted mb-4">Highest price each segment will tolerate while maintaining at least {(threshold * 100).toFixed(0)}% purchase probability.</p>

      <ResponsiveContainer width="100%" height={isMobile ? 180 : 220}>
        <BarChart data={data} margin={{ top: 10, right: 30, left: 10, bottom: 10 }}>
          <CartesianGrid {...GRID_PROPS} />
          <XAxis dataKey="segmentName" {...X_AXIS_PROPS} tick={{ fill: 'var(--color-axis-tick)', fontSize: 11 }} />
          <YAxis {...Y_AXIS_PROPS} tickFormatter={v => formatPrice(v)} domain={[0, 2000]} />
          <Tooltip
            {...TOOLTIP_STYLE}
            formatter={(value: number) => [
              value === 0 ? `N/A — below ${(threshold * 100).toFixed(0)}% threshold` : formatPrice(value),
              'Max Price',
            ]}
          />
          <Bar dataKey="ceiling" radius={[4, 4, 0, 0]} maxBarSize={60} label={<BarLabel />}>
            {data.map((entry, i) => (
              <Cell key={i} fill={entry.color} fillOpacity={entry.ceiling === 0 ? 0.25 : 0.8} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      <div className="mt-3 grid grid-cols-3 gap-3 text-xs">
        {data.map(seg => {
          const isZero = seg.ceiling === 0;
          return (
            <div key={seg.segmentName} className="text-center">
              <div className="font-semibold text-primary">{seg.segmentName}</div>
              <div className="text-muted">
                {isZero
                  ? <>Peak: <span className="font-mono">{formatPercent(seg.peakDemand!)}</span> at <span className="font-mono">{formatPrice(seg.peakDemandPrice!)}</span> — below <span className="font-mono">{(threshold * 100).toFixed(0)}%</span> threshold</>
                  : <>Demand at ceiling: <span className="font-mono">{formatPercent(seg.demandAtCeiling)}</span></>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
